// src/pages/admin/Currencies.jsx
import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Tooltip,
} from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import { motion } from "framer-motion";
import {
  fetchCurrencies,
  createCurrency,
  updateCurrency,
  deleteCurrency,
} from "../utils/api"; // adjust path if needed

export default function CurrenciesPage() {
  const [currencies, setCurrencies] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [editCurrency, setEditCurrency] = useState(null);
  const [newCurrency, setNewCurrency] = useState({
    code: "",
    name: "",
    symbol: "",
  });
  const [modalOpen, setModalOpen] = useState(false);

  // Fetch from backend
  useEffect(() => {
    loadCurrencies();
  }, []);

  const loadCurrencies = async () => {
    try {
      const res = await fetchCurrencies();
      setCurrencies(res.data);
    } catch (err) {
      console.error("Failed to load currencies:", err);
    }
  };

  const handleSaveEdit = async () => {
    try {
      const res = await updateCurrency(editCurrency.id, editCurrency);
      setCurrencies((prev) =>
        prev.map((c) => (c.id === editCurrency.id ? res.data.currency : c))
      );
      setEditCurrency(null);
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "Currency updated", severity: "success" },
        })
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleAdd = async () => {
    try {
      const res = await createCurrency(newCurrency);
      setCurrencies((prev) => [...prev, res.data.currency]);
      setNewCurrency({ code: "", name: "", symbol: "" });
      setModalOpen(false);
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "New currency added", severity: "success" },
        })
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCurrency(id);
      setCurrencies((prev) => prev.filter((c) => c.id !== id));
      window.dispatchEvent(
        new CustomEvent("showSnackbar", {
          detail: { message: "Currency deleted", severity: "error" },
        })
      );
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Typography variant="h5" sx={{ color: "#0ff", fontWeight: "bold" }}>
          Currencies
        </Typography>
        <Button
          startIcon={<Add />}
          variant="contained"
          color="primary"
          onClick={() => setModalOpen(true)}
        >
          Add Currency
        </Button>
      </Box>

      {/* Table */}
      <Card
        sx={{
          bgcolor: "rgba(25,25,50,0.7)",
          border: "1px solid #0ff4",
          borderRadius: 3,
          backdropFilter: "blur(12px)",
          overflow: "hidden",
        }}
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Code</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Symbol</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currencies
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((currency, index) => (
                  <motion.tr
                    key={currency.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <TableCell>
                      <Chip
                        label={currency.code}
                        sx={{
                          bgcolor: "rgba(0,255,255,0.2)",
                          color: "#0ff",
                          fontWeight: "bold",
                          boxShadow: "0 0 6px cyan",
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {editCurrency?.id === currency.id ? (
                        <TextField
                          size="small"
                          value={editCurrency.name}
                          onChange={(e) =>
                            setEditCurrency({
                              ...editCurrency,
                              name: e.target.value,
                            })
                          }
                          sx={{ input: { color: "#fff" } }}
                        />
                      ) : (
                        currency.name
                      )}
                    </TableCell>
                    <TableCell>
                      {editCurrency?.id === currency.id ? (
                        <TextField
                          size="small"
                          value={editCurrency.symbol}
                          onChange={(e) =>
                            setEditCurrency({
                              ...editCurrency,
                              symbol: e.target.value,
                            })
                          }
                          sx={{ input: { color: "#fff" } }}
                        />
                      ) : (
                        currency.symbol
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {editCurrency?.id === currency.id ? (
                        <Button
                          variant="contained"
                          color="success"
                          onClick={handleSaveEdit}
                        >
                          Save
                        </Button>
                      ) : (
                        <>
                          <Tooltip title="Edit">
                            <IconButton
                              color="info"
                              onClick={() => setEditCurrency(currency)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              color="error"
                              onClick={() => handleDelete(currency.id)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </TableCell>
                  </motion.tr>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={currencies.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Card>

      {/* Add Currency Modal */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)}>
        <DialogTitle>Add New Currency</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Code"
            value={newCurrency.code}
            onChange={(e) =>
              setNewCurrency({ ...newCurrency, code: e.target.value })
            }
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Name"
            value={newCurrency.name}
            onChange={(e) =>
              setNewCurrency({ ...newCurrency, name: e.target.value })
            }
            sx={{ mt: 2 }}
          />
          <TextField
            fullWidth
            label="Symbol"
            value={newCurrency.symbol}
            onChange={(e) =>
              setNewCurrency({ ...newCurrency, symbol: e.target.value })
            }
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalOpen(false)} color="error">
            Cancel
          </Button>
          <Button onClick={handleAdd} variant="contained" color="success">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}